
import { useState, useRef, useCallback, useEffect } from 'react';
import { useToast } from "@/components/ui/use-toast";
import { createNoiseCancellationProcessor } from '@/utils/audio-processors';
import { 
  getSpeechRecognition,
  type SpeechRecognition,
  type SpeechRecognitionEvent,
  type SpeechRecognitionErrorEvent
} from '@/types/speechRecognition';

import { 
  TranscriptionConfidence, 
  AudioQualityMetrics, 
  EnvironmentAnalysis, 
  UseSpeechToTextAdvancedProps 
} from './speech-to-text/types';
import { useAudioQualityMonitor } from './speech-to-text/useAudioQualityMonitor';
import { useEnvironmentAnalysis } from './speech-to-text/useEnvironmentAnalysis';
import { useNoiseCalibration } from './speech-to-text/useNoiseCalibration';
import { useOpenAIProcessing } from './speech-to-text/useOpenAIProcessing';

export default function useSpeechToTextAdvanced({
  onResult,
  enableNoiseReduction = true,
  enableQualityMonitoring = false,
  enableEnvironmentAdaptation = false,
  enableOpenAIFallback = false,
}: UseSpeechToTextAdvancedProps) {
  const [text, setText] = useState('');
  const [isListening, setIsListening] = useState(false);
  const [confidence, setConfidence] = useState<TranscriptionConfidence | null>(null);
  const [error, setError] = useState<any>(null);
  const { toast } = useToast();

  const recognitionRef = useRef<SpeechRecognition | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const audioContextRef = useRef<AudioContext | null>(null);
  const noiseCancellationProcessorRef = useRef<AudioWorkletNode | null>(null);

  // Custom hooks
  const { qualityMetrics, updateQualityMetrics, setAnalyser } = useAudioQualityMonitor();
  const { environmentAnalysis, analyzeAudioEnvironment } = useEnvironmentAnalysis();
  const { isCalibrating, startNoiseCalibration } = useNoiseCalibration();
  const { processWithOpenAI, retranscribe, isTranscribingRef } = useOpenAIProcessing();

  const calibrateNoise = useCallback(async () => {
    const analyser = audioContextRef.current?.createAnalyser() || null;
    await startNoiseCalibration(analyser);
  }, [startNoiseCalibration]);

  const startListening = useCallback(() => {
    const SpeechRecognitionClass = getSpeechRecognition();
    
    if (!SpeechRecognitionClass) {
      setError({ message: 'Speech recognition is not supported in this browser.' });
      return;
    }

    setIsListening(true);
    setError(null);
    setText('');
    setConfidence(null);

    const recognition = new SpeechRecognitionClass();
    recognitionRef.current = recognition;

    recognition.lang = 'pt-BR';
    recognition.continuous = true;
    recognition.interimResults = true;

    recognition.onstart = () => {
      console.log('Speech recognition started');
    };

    recognition.onresult = (event: SpeechRecognitionEvent) => {
      const last = event.results.length - 1;
      const currentText = event.results[last][0].transcript.trim();
      setText(currentText);

      const currentConfidence: TranscriptionConfidence = {
        text: currentText,
        overallConfidence: event.results[last][0].confidence,
        words: [],
        needsReprocessing: event.results[last][0].confidence < 0.6,
        isHighQuality: event.results[last][0].confidence > 0.8
      };
      setConfidence(currentConfidence);
    };

    recognition.onend = () => {
      console.log('Speech recognition ended');
      setIsListening(false);
    };

    recognition.onerror = (event: SpeechRecognitionErrorEvent) => {
      console.error('Speech recognition error:', event.error);
      setError(event.error);
      setIsListening(false);

      let errorMessage = 'Erro no reconhecimento de voz.';

      switch (event.error) {
        case 'not-allowed':
          errorMessage = 'Permissão negada para usar o microfone.';
          break;
        case 'no-speech':
          errorMessage = 'Nenhum discurso detectado.';
          break;
        case 'aborted':
          errorMessage = 'Reconhecimento de voz abortado.';
          break;
        case 'audio-capture':
          errorMessage = 'Erro ao capturar áudio.';
          break;
        case 'network':
          errorMessage = 'Erro de rede.';
          break;
        case 'service-not-allowed':
          errorMessage = 'Serviço de reconhecimento de voz não permitido.';
          break;
        case 'bad-grammar':
          errorMessage = 'Gramática inválida.';
          break;
        case 'language-not-supported':
          errorMessage = 'Linguagem não suportada.';
          break;
      }

      toast({
        title: "Erro",
        description: errorMessage,
        variant: "destructive"
      });
    };

    navigator.mediaDevices.getUserMedia({ audio: true })
      .then(stream => {
        streamRef.current = stream;

        if (enableQualityMonitoring) {
          const audioContext = new AudioContext();
          audioContextRef.current = audioContext;
          const source = audioContext.createMediaStreamSource(stream);
          const analyser = audioContext.createAnalyser();
          
          analyser.fftSize = 256;
          source.connect(analyser);
          setAnalyser(analyser);

          const updateMetrics = () => {
            updateQualityMetrics();
            requestAnimationFrame(updateMetrics);
          };

          updateMetrics();
        }

        if (enableNoiseReduction) {
          createNoiseCancellationProcessor(audioContextRef.current!)
            .then(noiseCancellationProcessor => {
              noiseCancellationProcessorRef.current = noiseCancellationProcessor;
              const source = audioContextRef.current!.createMediaStreamSource(stream);
              source.connect(noiseCancellationProcessorRef.current!).connect(audioContextRef.current!.destination);
              recognition.start();
            })
            .catch(err => {
              console.error('Error creating noise cancellation processor:', err);
              recognition.start();
            });
        } else {
          recognition.start();
        }
      })
      .catch(err => {
        console.error('Error getting user media:', err);
        setError(err);
        setIsListening(false);
        toast({
          title: "Erro",
          description: "Permissão negada para acessar o microfone.",
          variant: "destructive"
        });
      });

  }, [enableNoiseReduction, enableQualityMonitoring, toast, updateQualityMetrics, setAnalyser]);

  const stopListening = useCallback(() => {
    if (recognitionRef.current) {
      recognitionRef.current.stop();
      recognitionRef.current = null;
    }

    if (streamRef.current) {
      streamRef.current.getTracks().forEach(track => track.stop());
      streamRef.current = null;
    }

    if (audioContextRef.current && audioContextRef.current.state !== 'closed') {
      audioContextRef.current.close();
      audioContextRef.current = null;
    }

    if (noiseCancellationProcessorRef.current) {
      noiseCancellationProcessorRef.current.disconnect();
      noiseCancellationProcessorRef.current = null;
    }

    setIsListening(false);
    console.log('Speech recognition stopped');
  }, []);

  const resetText = useCallback(() => {
    setText('');
    setConfidence(null);
  }, []);

  const handleRetranscribe = useCallback(async () => {
    if (!text) return;
    
    try {
      const result = await retranscribe(text, onResult);
      if (result) {
        setText(result.text);
        setConfidence(result.confidence);
      }
    } catch (e) {
      setError(e);
    }
  }, [text, onResult, retranscribe]);

  useEffect(() => {
    if (enableEnvironmentAdaptation) {
      analyzeAudioEnvironment(qualityMetrics);
    }
  }, [enableEnvironmentAdaptation, analyzeAudioEnvironment, qualityMetrics]);

  useEffect(() => {
    if (text && confidence?.overallConfidence && !isTranscribingRef.current) {
      if (confidence.needsReprocessing && enableOpenAIFallback) {
        isTranscribingRef.current = true;
        processWithOpenAI(text)
          .then(result => {
            onResult(result.text, result.confidence);
            setText(result.text);
            setConfidence(result.confidence);
          })
          .catch(e => {
            setError(e);
            toast({
              title: "Erro",
              description: "Não foi possível melhorar a transcrição.",
              variant: "destructive"
            });
            onResult(text, confidence);
          })
          .finally(() => {
            isTranscribingRef.current = false;
          });
      } else {
        onResult(text, confidence);
      }
    }
  }, [text, confidence, onResult, enableOpenAIFallback, processWithOpenAI, toast, isTranscribingRef]);

  const SpeechRecognitionClass = getSpeechRecognition();
  const hasRecognitionSupport = !!SpeechRecognitionClass;

  return {
    text,
    confidence,
    isListening,
    startListening,
    stopListening,
    resetText,
    hasRecognitionSupport,
    error,
    qualityMetrics,
    environmentAnalysis,
    retranscribe: handleRetranscribe,
    calibrateNoise,
    isCalibrating
  };
}
